<template>
	<div class="plan-free">
		<p class="plan-free__title">{{ FREE_PLAN_TITLE }}</p>
		<p class="plan-free__counter">
			{{ FREE_COUNTER_LABEL }}:
			<span class="plan-free__counter-value">{{ remaining }}</span>
			<span class="plan-free__counter-of">&nbsp;из&nbsp;</span>
			<span class="plan-free__counter-value">{{ limit }}</span>
		</p>

		<div class="plan-free__divider"></div>

		<p class="plan-free__pro-name">
			<span class="plan-free__pro-brand">{{ PRO_PLAN_WORDMARK.brand }}</span
			><span class="plan-free__pro-accent">{{ PRO_PLAN_WORDMARK.accent }}</span>
			<span class="plan-free__pro-suffix">&nbsp;{{ PRO_PLAN_WORDMARK.suffix }}</span>
		</p>
		<ul class="plan-free__list">
			<li v-for="feature in plansStore.features" :key="feature" class="plan-free__item">
				<svg-icon name="check" :size="24" />
				<span>{{ feature }}</span>
			</li>
		</ul>

		<base-button :text="plansStore.cta" @click="startCheckout('plan_card')" />
		<p class="plan-free__note">{{ plansStore.limitsNote }}</p>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { usePaymentFlow } from '@/composables/usePaymentFlow'
import { usePlansStore } from '@/stores/plans'
import { GUEST_FREE_LIMIT, useUsageStore } from '@/stores/usage'
import { FREE_COUNTER_LABEL, FREE_PLAN_TITLE, PRO_PLAN_WORDMARK } from '@/constants/pro'

const usageStore = useUsageStore()
const plansStore = usePlansStore()
const { startCheckout } = usePaymentFlow()

// Серверный срез авторитетен; гостевой остаток из cookie — фолбэк на случай, когда
// /usage ещё не пришёл (у авторизованного пользователя тогда покажется 0 из 0).
//
// Остаток считаем как limit - used, а не берём готовое поле remaining: правая цифра
// счётчика — это limit, и обе цифры обязаны приходить из одного места среза. Иначе
// расхождение внутри среза человек видит как «1 из 1» вместо «2 из 3».
const remaining = computed(() => {
	const text = usageStore.limits?.text
	if (!text) return usageStore.isGuest ? usageStore.guestRemaining : 0
	return Math.max(0, text.limit - text.used)
})
const limit = computed(() => usageStore.limits?.text.limit ?? (usageStore.isGuest ? GUEST_FREE_LIMIT : 0))
</script>

<style lang="scss" scoped>
.plan-free {
	@include flex-column-center;
	width: 100%;
	&__title {
		font: var(--font-heading);
		color: var(--text-primary);
		text-align: center;
		margin-bottom: 12px;
	}
	&__counter {
		font: var(--font-body-small);
		color: var(--text-secondary);
		text-align: center;
		margin-bottom: 16px;
	}
	&__counter-value {
		font: var(--font-label);
		color: var(--text-primary);
	}
	&__counter-of {
		color: var(--text-secondary);
	}
	&__divider {
		width: 100%;
		height: 1px;
		background-color: var(--border-subtle);
		margin-bottom: 16px;
	}
	&__pro-name {
		font: var(--font-title);
		text-align: center;
		margin-bottom: 16px;
	}
	&__pro-brand {
		color: var(--text-primary);
	}
	&__pro-accent {
		color: var(--text-brand);
	}
	&__pro-suffix {
		color: var(--text-secondary);
	}
	&__list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		list-style: none;
		width: 100%;
		margin-left: 0;
		margin-bottom: 24px;
	}
	&__item {
		font: var(--font-label);
		display: flex;
		align-items: flex-start;
		gap: 8px;
		color: var(--text-primary);
		.svg-icon {
			width: 24px;
			height: 24px;
			min-width: 24px;
			min-height: 24px;
			flex-shrink: 0;
			color: var(--text-secondary);
		}
	}
	// перебиваем padding: 15px 20px из .btn в BaseButton
	.btn {
		padding: 14px 12px;
		min-height: 52px;
		margin-bottom: 16px;
	}
	&__note {
		font: var(--font-caption);
		color: var(--text-secondary);
		text-align: center;
	}
}
</style>
